/**
 * Cart Upsell Engine - Performance Optimized
 * Moves complex upsell logic from Liquid to JavaScript for better performance
 */

import type { AllowedType } from "../modules/bundle-storefront";
import { trackIntelligemsEvent } from "../utils/analytics";

class CartUpsellEngine {
  private cart: Cart | null = null;
  private upsellContainer: HTMLElement | null = null;
  private isLoading = false;
  private cache = new Map<string, { data: unknown; timestamp: number }>();
  private productRequests = new Map<string, Promise<ShopifyProduct | null>>();
  private lastCartHash: string | null = null;
  private upsellsLoaded = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshQueued = false;
  // Bumped on every cart change; a render started under an older generation never counts as up to date.
  private cartGeneration = 0;
  // Full cart carried by the last pub/sub cartUpdate, consumed once by the next refresh to skip a /cart.js fetch.
  private payloadCart: Cart | null = null;
  private cartDrawerObserver: MutationObserver | null = null;
  private config = {
    maxUpsells: 3,
    cacheTimeout: 5 * 60 * 1000,
    forcedProductHandles: [] as string[],
    collections: {
      screenProtectors: "screen-protectors",
      lensProtectors: "lens-protectors",
      magsafeRings: "magsafe-rings",
      chargepods: "chargepods",
      accessories: "phone-accessories",
    },
    products: {
      screen: "screens",
      lens: "camera-lens-protector",
      magsafeRing: "magsafe-ring",
      chargepod: "classic-chargepod",
    },
  };

  constructor() {
    this.init();
  }

  init(): void {
    this.config.forcedProductHandles = Array.isArray(window.cartUpsellForcedProducts) ? window.cartUpsellForcedProducts : [];

    // Only initialize when cart drawer is opened
    document.addEventListener("cart-drawer:open", () => {
      this.requestUpsellRefresh(true);
    });

    // Listen for cart updates
    document.addEventListener("cart:updated", (event) => {
      // No cart data on this event: any payload cart seen earlier may be older than this change.
      this.payloadCart = null;
      if ((event as CustomEvent).detail?.source === "upsell-engine") {
        // An upsell-card add: leave the clicked card (and its "Added!" label) on screen briefly
        // before the loader replaces the upsells.
        this.cartGeneration++;
        this.upsellsLoaded = false;
        setTimeout(() => this.requestUpsellRefresh(), 1200);
        return;
      }
      this.refreshAfterCartChange();
    });

    // Also listen for pub/sub cart updates. Drawer line edits (remove, quantity) go through
    // CartDrawerItems in global.ts, which publishes cartUpdate.
    if (typeof subscribe !== "undefined" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event: unknown) => {
        // Only a full cart object is reusable (add.js responses are a single line item).
        const cartData = (event as { cartData?: Cart } | undefined)?.cartData;
        this.payloadCart = cartData && Array.isArray(cartData.items) && typeof cartData.item_count === "number" ? cartData : null;
        this.refreshAfterCartChange();
      });
    }

    // Listen for cart drawer specific events that might indicate cart changes
    document.addEventListener("cart-drawer:refresh", () => {
      if (this.isCartDrawerOpen()) this.refreshAfterCartChange();
    });

    // Watch for cart drawer content changes that might overwrite our upsells
    this.setupCartDrawerObserver();
  }

  setupCartDrawerObserver(): void {
    const cartDrawer = document.querySelector("cart-drawer") || document.querySelector("#CartDrawer");
    if (!cartDrawer) return;

    this.cartDrawerObserver?.disconnect();

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    this.cartDrawerObserver = new MutationObserver(() => {
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        if (!this.isCartDrawerOpen()) return;
        // A drawer re-render can leave #cart-upsells empty while the cart is unchanged.
        // Checking the DOM first avoids a /cart.js fetch on every drawer mutation.
        const upsellContainer = document.getElementById("cart-upsells");
        if (upsellContainer && !this.hasRenderedContent(upsellContainer)) this.requestUpsellRefresh();
      }, 500);
    });

    this.cartDrawerObserver.observe(cartDrawer, {
      childList: true,
      subtree: true,
    });
  }

  destroyObserver(): void {
    this.cartDrawerObserver?.disconnect();
    this.cartDrawerObserver = null;
  }

  debounce(func: (...args: unknown[]) => void, wait: number): (...args: unknown[]) => void {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: unknown[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  generateCartHash(cart: Cart): string {
    if (!cart || !cart.items || cart.items.length === 0) return "empty";

    // Full (untruncated) signature of lines and quantities. item.key also covers line properties.
    return cart.items
      .map((item) => `${item.key ?? item.variant_id}:${item.quantity}`)
      .sort()
      .join("|");
  }

  // The server renders #cart-upsells with only whitespace and a comment, so child NODES say nothing:
  // look for real elements (cards, sections, the loader or the empty-render marker).
  hasRenderedContent(container: HTMLElement | null): boolean {
    return !!container?.querySelector(":scope > *");
  }

  // A legitimately empty render (or a failed one) must not look like "container wiped by a drawer
  // re-render" to the observer and the up-to-date check, or it would reload forever.
  markEmptyUpsells(container: HTMLElement | null): void {
    if (container && !this.hasRenderedContent(container)) {
      container.innerHTML = '<div class="cart-upsells-empty" hidden></div>';
    }
  }

  // Single entry point for every upsell reload. Bursts of triggers (drawer open + pub/sub
  // cartUpdate + cart:updated land within ~100ms of each other) collapse into one run, and a
  // request that arrives while a run is in flight is replayed once afterwards instead of dropped.
  requestUpsellRefresh(fromOpen = false): void {
    // The refresh itself only starts after the debounce and a /cart.js round-trip: show the
    // loading indicator right away whenever the upsells are known to be stale, otherwise the
    // drawer shows an empty or outdated block for that whole time.
    // fromOpen: the drawer dispatches cart-drawer:open before it is flagged open, so isCartDrawerOpen()
    // is not reliable for that event. Other triggers while the drawer is closed leave the container alone.
    if (!this.upsellsLoaded && (fromOpen || this.isCartDrawerOpen()) && this.reacquireUpsellContainer()) {
      this.showLoadingState();
    }
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.runUpsellRefresh();
    }, 150);
  }

  // Cart contents changed: force the next refresh even if the hash happens to match.
  // Product JSON, best sellers and same-name search results don't depend on the cart, so
  // they keep their normal TTL (config.cacheTimeout); per-cart filtering happens at render time.
  refreshAfterCartChange(): void {
    this.cartGeneration++;
    this.upsellsLoaded = false;
    this.requestUpsellRefresh();
  }

  private async runUpsellRefresh(): Promise<void> {
    if (this.isLoading) {
      this.refreshQueued = true;
      return;
    }
    // Consume the payload cart now: if this run bails, it would be stale by the next open.
    const payloadCart = this.payloadCart;
    this.payloadCart = null;
    // A closed drawer re-checks on its next open (cart-drawer:open -> requestUpsellRefresh).
    if (!this.isCartDrawerOpen()) return;

    this.isLoading = true;
    try {
      const generation = this.cartGeneration;
      const cart = payloadCart ?? (await this.getCart());
      const hash = this.generateCartHash(cart);
      const container = document.getElementById("cart-upsells");
      const upToDate =
        this.upsellsLoaded &&
        hash === this.lastCartHash &&
        this.hasRenderedContent(container) &&
        !container?.querySelector(".cart-upsells-loading");
      if (!upToDate) {
        await this.loadUpsells(cart, generation);
        this.lastCartHash = hash;
      }
    } catch (error) {
      console.warn("Upsell refresh failed:", error);
      // The loader may already be on screen (requestUpsellRefresh shows it up front): clear it.
      // upsellsLoaded stays false, so the next open or cart change retries.
      this.hideLoadingState();
      this.markEmptyUpsells(document.getElementById("cart-upsells"));
    } finally {
      this.isLoading = false;
      if (this.refreshQueued) {
        this.refreshQueued = false;
        this.requestUpsellRefresh();
      }
    }
  }

  async loadUpsells(cart: Cart, generation = this.cartGeneration): Promise<void> {
    this.upsellsLoaded = false;
    this.upsellContainer = document.getElementById("cart-upsells");
    if (!this.upsellContainer) return;

    try {
      this.showLoadingState();
      this.cart = cart;
      if (this.cart?.items.length === 0) {
        await this.renderEmptyCartUpsells();
      } else {
        await this.renderCartUpsells();
      }
      this.initializeUpsellForms();
      // A cart change during this render bumped cartGeneration: stay stale so the replay re-renders.
      this.upsellsLoaded = generation === this.cartGeneration;
    } catch (error) {
      this.hideLoadingState();
      this.markEmptyUpsells(this.upsellContainer);
    }
  }

  initializeUpsellForms(): void {
    // Find the upsell container (it might not be cached yet)
    const upsellContainer = this.upsellContainer || document.getElementById("cart-upsells");

    if (!upsellContainer) {
      return;
    }

    // Find all product-form elements in the upsell container
    const upsellForms = upsellContainer.querySelectorAll<ProductFormElement>("product-form");
    const cartDrawer = document.querySelector("cart-drawer");

    upsellForms.forEach((form, index) => {
      // Set the cart reference that ProductForm expects
      if (cartDrawer && !form.cart) {
        form.cart = cartDrawer;
      }

      // Add event listener to handle form submission via AJAX
      const formElement = form.querySelector('form[data-type="add-to-cart-form"]');

      if (formElement && !formElement.hasAttribute("data-upsell-initialized")) {
        formElement.setAttribute("data-upsell-initialized", "true");

        // Disable the product-form custom element's default behavior
        if (form.onSubmitHandler) {
          form.onSubmitHandler = null;
        }

        // Add event listener with high priority (capture phase)
        formElement.addEventListener("submit", this.handleUpsellSubmit.bind(this), true);

        // Also add to the button directly to ensure it's caught
        const submitButton = formElement.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.addEventListener("click", this.handleUpsellButtonClick.bind(this), true);
        }
      } else if (formElement && formElement.hasAttribute("data-upsell-initialized")) {
      } else {
        console.warn("❌ No form element found in upsell form");
      }
    });
  }

  async handleUpsellButtonClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const button = (event.target as Element).closest('button[type="submit"]') as HTMLButtonElement;
    const form = button.closest("form") as HTMLFormElement;

    await this.submitUpsellForm(form, button);
  }

  async handleUpsellSubmit(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target as HTMLFormElement;
    const button = form.querySelector('button[type="submit"]') as HTMLButtonElement;

    await this.submitUpsellForm(form, button);
  }

  async submitUpsellForm(form: HTMLFormElement, button: HTMLButtonElement): Promise<void> {
    const spinner = button.querySelector(".loading__spinner") as HTMLElement | null;
    const buttonText = button.querySelector("span") as HTMLSpanElement;

    // Show loading state
    if (spinner) spinner.classList.remove("hidden");
    button.disabled = true;
    const originalText = buttonText.textContent;
    buttonText.textContent = this.getLocalizedText("adding") + "...";

    try {
      const formData = new FormData(form);
      // Section Rendering API: the add response carries the re-rendered drawer, so no
      // follow-up section fetch or /cart.js round-trips are needed. sections_url keeps
      // the query string so theme preview / theme dev context params reach the renderer.
      formData.append("sections", "cart-drawer-optimized-v2");
      formData.append("sections_url", window.location.pathname + window.location.search);

      const response = await fetch("/cart/add.js", { method: "POST", body: formData });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      trackIntelligemsEvent("add_to_cart_upsell", {
        variant_id: result?.variant_id ?? result?.id ?? 0,
        item_name: result?.product_title ?? result?.title ?? "",
        quantity: result?.quantity ?? 1,
        value: Math.round(Number(result?.final_line_price ?? 0)) / 100,
        source: "cart_drawer_upsell",
      });
      let sectionHtml: string | undefined = result?.sections?.["cart-drawer-optimized-v2"];
      if (!sectionHtml) {
        // The add succeeded but the section did not come back: fetch it once so the drawer
        // does not stay stale. Never throws, a failed add is the only "Error" case.
        sectionHtml = await this.fetchCartDrawerSection();
      }
      if (sectionHtml) this.applyCartDrawerSection(sectionHtml);

      // Show success feedback before notifying listeners, so the label is on screen when they run.
      buttonText.textContent = this.getLocalizedText("added");
      setTimeout(() => {
        buttonText.textContent = originalText;
      }, 2000);

      // Header bubble, kit/bundle/gift regrouping and (after a short delay) the upsell refresh.
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          // cartData: the /cart/add.js response, read by klaviyo-added-to-cart.ts
          detail: { source: "upsell-engine", cartData: result },
        }),
      );

      // Dispatches cart:updated only; pub/sub subscribers (gift-with-purchase, price-per-item) do not hear upsell-card adds.
    } catch (error) {
      buttonText.textContent = this.getLocalizedText("error_retry");
      setTimeout(() => {
        buttonText.textContent = originalText;
      }, 3000);
    } finally {
      // Hide loading state
      if (spinner) spinner.classList.add("hidden");
      button.disabled = false;
    }
  }

  async getCart() {
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, 300 * attempt));
      }
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
        const cart = await response.json();
        return cart;
      } catch (e) {
        if (attempt === 2) throw e;
      }
    }
  }

  // A full cart-drawer re-render (product-form's own add, or gift-with-purchase's
  // addGiftLine) replaces .drawer__inner's innerHTML wholesale, detaching whatever
  // #cart-upsells node was captured before the async fetches above ran. Re-query
  // right before writing so we target the current (possibly recreated) node.
  reacquireUpsellContainer(): HTMLElement | null {
    if (!this.upsellContainer?.isConnected) {
      this.upsellContainer = document.getElementById("cart-upsells");
    }
    return this.upsellContainer;
  }

  async renderEmptyCartUpsells(): Promise<void> {
    const products = await this.getRecommendedProducts(3);

    if (products.length === 0) {
      const container = this.reacquireUpsellContainer();
      if (!container) return;
      container.innerHTML = `
        <div class="cart-drawer__empty-upsell--inner">
          <p>${this.getLocalizedText("no_recommendations")}</p>
        </div>
      `;
      return;
    }

    const html = products
      .map((product, index) => {
        return this.renderUpsellCard(product, "default", null);
      })
      .join("");

    const finalHTML = `
      <div class="cart-drawer__empty-upsell--inner">
        ${html}
      </div>
    `;

    const container = this.reacquireUpsellContainer();
    if (!container) return;
    container.innerHTML = finalHTML;

    // Force the container to be visible
    container.style.display = "block";
    container.style.visibility = "visible";
    container.style.opacity = "1";

    // Also ensure the cart drawer doesn't hide upsells when empty
    const cartDrawer = document.querySelector("cart-drawer") || document.querySelector("#CartDrawer");
    if (cartDrawer) {
      cartDrawer.classList.remove("is-empty");
    }
  }

  async renderCartUpsells(): Promise<void> {
    // The Ajax cart lists the most recently added line first. Skip lines code adds on its own
    // (gift-with-purchase, free accessories) so they never become the anchor.
    const lastItem =
      this.cart!.items.find(
        (item) =>
          !item.properties?._gift_rule_id && ((item as { final_line_price?: number }).final_line_price ?? 1) > 0,
      ) ?? this.cart!.items[0];
    const cartAnalysis = this.analyzeCart();
    document.querySelector('.drawer__inner.is-empty')?.classList.remove('is-empty')

    // Find the first phone case in the cart for upsell logic
    const phoneCase = this.cart!.items.find((item) => this.isPhoneCase(item));

    const [recommendedProducts, sameNameProducts, phoneCaseHtml, ensembleHtml] = await Promise.all([
      // Over-fetch only when same-design products may be deduped out of the recommendations.
      this.getRecommendedProducts(window.cartUpsellSameNameEnabled ? 6 : 3).catch(() => [] as ShopifyProduct[]),
      this.getSameNameProducts(lastItem),
      phoneCase ? this.renderPhoneCaseUpsells(phoneCase, cartAnalysis) : Promise.resolve(""),
      this.hasEnsembleTag(lastItem) ? this.renderEnsembleUpsells(lastItem, cartAnalysis) : Promise.resolve(""),
    ]);

    // A product can only appear once in the drawer (card ids are derived from the product id).
    const sameNameIds = new Set(sameNameProducts.map((product) => product.id));
    const recommendations = recommendedProducts.filter((product) => !sameNameIds.has(product.id)).slice(0, 3);
    const sameNameHtml = this.renderUpsellSection("same_name_title", sameNameProducts, "same-name", lastItem);
    const recommendationsHtml = this.renderUpsellSection("rec_title", recommendations, "recommendation", lastItem);

    // Order: same-design products first (most specific to what was just added), otherwise the
    // recommendations (which carry any merchant-forced products). "Complete your kit" is always
    // second, then ensemble, then whatever recommendations haven't been placed yet.
    const recommendationsLead = !sameNameHtml;
    const html =
      (sameNameHtml || recommendationsHtml) +
      phoneCaseHtml +
      ensembleHtml +
      (recommendationsLead ? "" : recommendationsHtml);

    const container = this.reacquireUpsellContainer();
    if (!container) return;
    container.innerHTML = html;
    this.hideLoadingState();
    this.markEmptyUpsells(container);
  }

  async getBestSellers(limit = 3): Promise<ShopifyProduct[]> {
    const cacheKey = `best-sellers-${limit}`;
    const cached = this.getFromCache(cacheKey);

    if (cached) return (cached as ShopifyProduct[]).slice(0, limit);

    try {
      // Try multiple collection names in case 'best-sellers' doesn't exist
      const collectionNames = ["best-sellers", "bestsellers", "popular", "featured", "all"];
      let data = null;

      for (const collectionName of collectionNames) {
        try {
          const response = await fetch(`/collections/${collectionName}/products.json?limit=${limit}`);

          if (response.ok) {
            data = await response.json();
            break;
          } else {
            console.warn(`Collection ${collectionName} not found (${response.status})`);
          }
        } catch (err: unknown) {
          console.warn(`Error fetching ${collectionName}:`, (err as Error).message);
        }
      }

      if (!data || !data.products || data.products.length === 0) {
        return [];
      }

      this.setCache(cacheKey, data.products);
      return data.products;
    } catch (error) {
      return [];
    }
  }

  async getForcedProducts(): Promise<ShopifyProduct[]> {
    const handles = this.config.forcedProductHandles;
    if (handles.length === 0) return [];

    const cartHandles = new Set(this.cart?.items.map((item) => item.handle) ?? []);
    const products = await Promise.all(handles.map((handle) => this.getProductByHandle(handle)));
    const seen = new Set<number>();

    return products.filter((product): product is ShopifyProduct => {
      if (!product || seen.has(product.id) || cartHandles.has(product.handle)) return false;
      seen.add(product.id);
      return true;
    });
  }

  async getRecommendedProducts(limit = 3): Promise<ShopifyProduct[]> {
    const forced = await this.getForcedProducts();
    const remaining = Math.max(limit - forced.length, 0);

    if (remaining === 0) return forced.slice(0, limit);

    const bestSellers = await this.getBestSellers(remaining);
    const forcedIds = new Set(forced.map((product) => product.id));

    return [...forced, ...bestSellers.filter((product) => !forcedIds.has(product.id))].slice(0, limit);
  }

  async renderEnsembleUpsells(lastItem: CartItem, cartAnalysis: CartAnalysis): Promise<string> {
    // Simplified ensemble logic - you can expand this based on your needs
    const title = this.getLocalizedText("ensemble_title");

    return `
      <div class="cart-drawer__upsell-section">
        <div class="cart-drawer__upsell-title">
          <h2>${title}</h2>
        </div>
        <div class="cart-drawer__upsell-products">
          <!-- Ensemble products would go here -->
        </div>
      </div>
    `;
  }

  renderUpsellSection(titleKey: string, products: ShopifyProduct[], type: string, cartProduct: CartItem): string {
    const html = products.map((product) => this.renderUpsellCard(product, type, cartProduct)).join("");
    if (!html) return "";
    return `
      <div class="cart-drawer__upsell-section">
        <div class="cart-drawer__upsell-title">
          <h2>${this.getLocalizedText(titleKey)}</h2>
        </div>
        <div class="cart-drawer__upsell-products">
          ${html}
        </div>
      </div>
    `;
  }

  // Same-title products of the other bundle product types (e.g. the case the customer
  // just added, offered as a clear case / folio / ...). Plain add-to-cart with a variant
  // select, no bundle. The Storefront search is memoized per anchor product.
  async getSameNameProducts(anchor: CartItem): Promise<ShopifyProduct[]> {
    if (!window.cartUpsellSameNameEnabled) return [];
    try {
      const allTypes = this.readAllowedTypes();
      const allowedTypes = allTypes.filter((t) => t.typeHandle !== anchor.product_type);
      if (!anchor.product_title || !allowedTypes.length) return [];

      const cacheKey = `same-name-${anchor.product_id}`;
      let candidates = this.getFromCache(cacheKey) as { handle: string; type: string }[] | undefined;
      if (!candidates) {
        // Provided by global.ts (module); this file is a classic script and cannot import it.
        if (!window.findSameNameProducts) return [];
        const nodes = await window.findSameNameProducts({
          title: anchor.product_title,
          excludeProductId: anchor.product_id,
          allowedTypes,
          typeWordSource: allTypes,
          lang: (document.documentElement.lang || "en").toUpperCase() === "FR" ? "FR" : "EN",
        });
        candidates = nodes.filter((n) => n.availableForSale !== false).map((n) => ({ handle: n.handle, type: n.productType }));
        this.setCache(cacheKey, candidates);
      }

      // Filter on what the search already knows before fetching product JSON, then fetch only
      // as many as needed for 3 cards.
      const cartHandles = new Set(this.cart!.items.map((i) => i.handle));
      const cartTypes = new Set(this.cart!.items.map((i) => i.product_type));
      const pending = candidates.filter((c) => !cartHandles.has(c.handle) && !cartTypes.has(c.type));
      const products: ShopifyProduct[] = [];
      while (products.length < 3 && pending.length) {
        const batch = pending.splice(0, 3 - products.length);
        const fetched = await Promise.all(batch.map((c) => this.getProductByHandle(c.handle)));
        products.push(...fetched.filter((p): p is ShopifyProduct => !!p));
      }
      return products;
    } catch (error) {
      console.error("Failed to load same-name upsells:", error);
      return [];
    }
  }

  readAllowedTypes(): AllowedType[] {
    const el = document.getElementById("cart-drawer-allowed-types-data");
    if (!el?.textContent) return [];
    try {
      return JSON.parse(el.textContent) as AllowedType[];
    } catch {
      return [];
    }
  }

  async renderPhoneCaseUpsells(lastItem: CartItem, cartAnalysis: CartAnalysis): Promise<string> {
    const upsells = [];
    const phoneModel = this.getPhoneModel(lastItem);

    const needsScreen = !cartAnalysis.hasScreenProtector && !!phoneModel;
    const needsLens = !cartAnalysis.hasLens && !!phoneModel;
    const needsMagsafe = !cartAnalysis.hasMagsafeRing && this.requiresMagsafe(lastItem);
    const needsChargepod = !cartAnalysis.hasChargepods;

    const [screenProduct, lensProduct, magsafeProduct, chargepodProduct] = await Promise.all([
      needsScreen ? this.getProductByHandle(this.config.products.screen) : null,
      needsLens ? this.getProductByHandle(this.config.products.lens) : null,
      needsMagsafe ? this.getProductByHandle(this.config.products.magsafeRing) : null,
      needsChargepod ? this.getProductByHandle(this.config.products.chargepod) : null,
    ]);

    // Same order and rules as before; only the fetching is parallel.
    if (screenProduct && phoneModel && this.hasCompatibleVariant(screenProduct, phoneModel)) {
      upsells.push({ product: screenProduct, type: "screen", cartProduct: lastItem });
    }
    if (lensProduct && phoneModel && this.hasCompatibleVariant(lensProduct, phoneModel)) {
      upsells.push({ product: lensProduct, type: "lens", cartProduct: lastItem });
    }
    if (magsafeProduct) {
      upsells.push({ product: magsafeProduct, type: "magsafe" });
    }
    if (chargepodProduct && upsells.length < this.config.maxUpsells) {
      upsells.push({ product: chargepodProduct, type: "chargepod" });
    }

    if (upsells.length === 0) return "";

    const title = this.getLocalizedText("pc_title");
    const html = upsells.map(({ product, type, cartProduct }) => this.renderUpsellCard(product, type, cartProduct || lastItem)).join("");

    return `
      <div class="cart-drawer__upsell-section">
        <div class="cart-drawer__upsell-title">
          <h2>${title}</h2>
        </div>
        <div class="cart-drawer__upsell-products">
          ${html}
        </div>
      </div>
    `;
  }

  analyzeCart(): CartAnalysis {
    const analysis = {
      hasScreenProtector: false,
      hasLens: false,
      hasMagsafeRing: false,
      hasChargepods: false,
      phoneModels: new Set<string>(),
      productHandles: new Set<string>(),
    };
    
    this.cart?.items.forEach(item => {
      const handle = item.handle || '';
      analysis.productHandles.add(handle);

      if (handle.includes("screen") || item.product_title?.toLowerCase().includes("screen")) {
        analysis.hasScreenProtector = true;
      }
      if (handle.includes("lens") || item.product_title?.toLowerCase().includes("lens")) {
        analysis.hasLens = true;
      }
      if (handle.includes("magsafe") || item.product_title?.toLowerCase().includes("magsafe")) {
        analysis.hasMagsafeRing = true;
      }
      if (handle.includes("chargepod") || item.product_title?.toLowerCase().includes("chargepod") || item.product_type?.toLowerCase().includes("chargepod")) {
        analysis.hasChargepods = true;
      }

      const phoneModel = this.getPhoneModel(item);
      if (phoneModel) {
        analysis.phoneModels.add(phoneModel);
      }
    });

    return analysis;
  }

  renderUpsellCard(product: ShopifyProduct, type = "default", cartProduct: CartItem | null = null): string {
    if (!product) return "";

    // Get phone model from cart product for variant matching
    const phoneModel = cartProduct ? this.getPhoneModel(cartProduct) : null;

    // Use compatible variant if phone model is available, otherwise use first variant
    const variant = phoneModel ? this.getCompatibleVariant(product, phoneModel) : product.variants?.[0];
    if (!variant) return "";

    const formID = `product-form-template--${product.id}__main`;

    // Store reference to formatMoneyUpsell method to avoid scope issues in template string
    const formatPrice = this.formatMoneyUpsell.bind(this);

    // Get the correct image URL from Shopify product data
    let imageUrl = null;

    if (product.type === "ChargePods" && product.images && product.images.length > 1) {
      // Use second image for ChargePods
      imageUrl = product.images[1];
    } else if (product.featured_image) {
      // Use featured_image if available
      imageUrl = product.featured_image;
    } else if (product.images && product.images.length > 0) {
      // Fallback to first image
      imageUrl = product.images[0];
    }

    // Extract URL from image object if needed
    if (imageUrl && typeof imageUrl === "object") {
      // Shopify image objects have different possible URL properties
      imageUrl = imageUrl.src || imageUrl.url || imageUrl.original_src || null;
    }

    // Generate variant options if multiple variants exist
    let variantOptions = "";
    if (product.variants && product.variants.length > 1) {
      variantOptions = `
        <select name="product-options" onchange="handleUpsellProductChange(event)">
          ${product.variants
            .map((v) => {
              // Generate phone model list for data attribute (matching original)
              let phoneModelList = "";
              if (v.metafields?.custom?.phone_model?.value) {
                const models = Array.isArray(v.metafields.custom.phone_model.value) ? v.metafields.custom.phone_model.value : [v.metafields.custom.phone_model.value];
                phoneModelList = models.join("|");
              }

              return `
              <option 
                value="${v.id}" 
                data-available="${v.available}" 
                data-phone-model="${phoneModelList}"
                ${v.id === variant.id ? "selected" : ""}
                ${!v.available ? "disabled" : ""}
              >
                ${v.title}
              </option>
            `;
            })
            .join("")}
        </select>
      `;
    }

    // Liquid 'loading-spinner' snippet (pastille_loading.svg); no custom spinner fallback.
    const loadingSpinner = window.cartUpsellStrings?.loadingSpinnerHidden ?? "";

    let price = formatPrice(variant.price);
    const discountPriceLocale = document.documentElement.lang || "en";
    const enPriceTag = discountPriceLocale == "en" ? "$" : "";
    const frPriceTag = discountPriceLocale == "fr" ? "$" : "";
    price = enPriceTag + price + frPriceTag;

    return `
      <div class="upsell-product upsell-product--default" data-type="${product.type}">
        <div class="img-container">
          ${imageUrl ? `<img src="${this.getImageUrl(imageUrl, 120)}" alt="${product.title}" loading="lazy">` : ""}
        </div>
        <div class="content-container">
          <h2><a href="/products/${product.handle}">${product.title}</a></h2>
          <div class="price">
            <span class="price-item price-item--regular">${price}</span>
          </div>
          ${variantOptions}
        </div>
        <div class="action-container">
          <product-form
            class="product-form"
            data-hide-errors="false"
            data-section-id="${formID}"
          >
            <div class="product-form__error-message-wrapper" role="alert" hidden>
              <svg aria-hidden="true" focusable="false" class="icon icon-error" viewBox="0 0 13 13">
                <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"/>
                <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"/>
                <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 10.0109C7.06121 10.0109 7.5317 9.57872 7.5317 9.00504C7.5317 8.43137 7.06121 7.99918 6.50076 7.99918C5.94031 7.99918 5.46982 8.43137 5.46982 9.00504C5.46982 9.57872 5.94031 10.0109 6.50076 10.0109Z" fill="white"/>
              </svg>
              <span class="product-form__error-message"></span>
            </div>
            
            <form action="/cart/add" method="post" enctype="multipart/form-data" novalidate="novalidate" class="form" data-type="add-to-cart-form" id="${formID}">
              <input type="hidden" name="id" value="${variant.id}" class="product-variant-id">
              <input type="hidden" name="quantity" value="1">
              
              <div class="product-form__buttons">
                <button
                  id="ProductSubmitButton-${product.id}"
                  type="submit"
                  name="add"
                  class="button button--fifth"
                  ${!variant.available ? "disabled" : ""}
                  aria-haspopup="dialog"
                  aria-labelledby="cart-notification-button-${variant.id}"
                  aria-live="polite"
                  data-sold-out-message="true"
                >
                  <span>
                    ${!variant.available ? this.getLocalizedText("sold_out") : this.getLocalizedText("add_to_cart")}
                  </span>
                  ${loadingSpinner}
                </button>
              </div>
            </form>
          </product-form>
        </div>
      </div>
    `;
  }

  // Helper methods
  isPhoneCase(item: CartItem): boolean {
    const phoneCaseTypes = ['Phone Case', 'Folio Case', 'Custom Phone Case - Folio', 'Custom Phone Case', 'Clear Case'];
    return phoneCaseTypes.includes(item.product_type) ||
           (item.handle || '').includes('case') ||
           (item.product_tags?.includes('phone-case') ?? false);
  }

  hasEnsembleTag(item: CartItem): boolean {
    return item.product_tags?.some((tag) => tag.includes("ensemble:")) ?? false;
  }

  getPhoneModel(item: CartItem): string | null {
    // Extract phone model from options_with_values
    const deviceOption = item.options_with_values?.find((opt) => opt.name === "Device" || opt.name === "Phone Model");

    if (deviceOption) {
      return deviceOption.value;
    }

    // Fallback: try variant_options array
    if (item.variant_options && item.variant_options.length > 0) {
      return item.variant_options[0];
    }
    return null;
  }

  requiresMagsafe(item: CartItem): boolean {
    // Check various ways MagSafe compatibility might be indicated
    const checks = [
      // Properties
      item.properties?.magsafe_required === "Yes",
      item.properties?.magsafe_compatible === "Yes",
      item.properties?.magsafe === "Yes",

      // Variant metafields
      item.variant_metafields?.magsafe_required === "Yes",
      item.variant_metafields?.magsafe_compatible === "Yes",

      // Product title/type checks
      item.product_title?.toLowerCase().includes("magsafe"),
      item.product_type === "Phone Case", // Assume all phone cases can use MagSafe rings

      // Variant title checks
      item.variant_title?.toLowerCase().includes("magsafe"),
    ];

    const result = checks.some((check) => check === true);

    return result;
  }

  async getProductByHandle(handle: string): Promise<ShopifyProduct | null> {
    if (!handle) return null;

    const cacheKey = `product-${handle}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached as ShopifyProduct;

    // Sections now render in parallel and can ask for the same handle at once
    // (e.g. a forced recommendation that is also a phone-case upsell): share one request.
    const inFlight = this.productRequests.get(handle);
    if (inFlight) return inFlight;

    const request = fetch(`/products/${handle}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((product: ShopifyProduct | null) => {
        if (product) this.setCache(cacheKey, product);
        return product;
      })
      .catch(() => null)
      .finally(() => this.productRequests.delete(handle));

    this.productRequests.set(handle, request);
    return request;
  }

  isExactPhoneModelMatch(variantModel: string, cartPhoneModel: string): boolean {
    if (!variantModel || !cartPhoneModel) return false;

    const normalize = (model: string) => model.toLowerCase().trim();
    const normalizedVariant = normalize(variantModel);
    const normalizedCart = normalize(cartPhoneModel);

    // Exact match first
    if (normalizedVariant === normalizedCart) return true;

    // Handle common variations (e.g., "iPhone 17" vs "17")
    const cartWithoutPrefix = normalizedCart.replace(/^(iphone\s*)/i, "");
    const variantWithoutPrefix = normalizedVariant.replace(/^(iphone\s*)/i, "");

    if (cartWithoutPrefix === variantWithoutPrefix) return true;

    // For any other case, require exact word-by-word match
    const cartWords = normalizedCart.split(/\s+/).filter((word) => word.length > 0);
    const variantWords = normalizedVariant.split(/\s+/).filter((word) => word.length > 0);

    // Must have same number of words
    if (cartWords.length !== variantWords.length) return false;

    // All words must match exactly in the same order
    for (let i = 0; i < cartWords.length; i++) {
      if (cartWords[i] !== variantWords[i]) return false;
    }

    return true;
  }

  hasCompatibleVariant(product: ShopifyProduct, phoneModel: string): boolean {
    if (!product || !phoneModel) return false;

    return product.variants?.some((variant: ProductVariant) => {
      // First check if variant is available (in stock)
      if (!variant.available) return false;

      // Check variant options with exact matching
      if (variant.options?.some((option) => this.isExactPhoneModelMatch(option, phoneModel))) {
        return true;
      }

      // Check variant title with exact matching
      if (this.isExactPhoneModelMatch(variant.title, phoneModel)) {
        return true;
      }

      // Check metafields for phone model compatibility
      if (variant.metafields?.custom?.phone_model?.value) {
        const phoneModels = Array.isArray(variant.metafields.custom.phone_model.value) ? variant.metafields.custom.phone_model.value : [variant.metafields.custom.phone_model.value];

        return phoneModels.some((model) => this.isExactPhoneModelMatch(model, phoneModel));
      }

      return false;
    }) ?? false;
  }

  getCompatibleVariant(product: ShopifyProduct, phoneModel: string): ProductVariant | undefined {
    if (!product || !phoneModel) {
      // Return first available variant if no phone model specified
      return product.variants?.find((v) => v.available) || product.variants?.[0];
    }

    // Find the variant that matches the phone model AND is available
    const compatibleVariant = product.variants?.find((variant) => {
      // First check if variant is available (in stock)
      if (!variant.available) return false;

      // Check variant options with exact matching
      if (variant.options?.some((option) => this.isExactPhoneModelMatch(option, phoneModel))) {
        return true;
      }

      // Check variant title with exact matching
      if (this.isExactPhoneModelMatch(variant.title, phoneModel)) {
        return true;
      }

      // Check metafields for phone model compatibility
      if (variant.metafields?.custom?.phone_model?.value) {
        const phoneModels = Array.isArray(variant.metafields.custom.phone_model.value) ? variant.metafields.custom.phone_model.value : [variant.metafields.custom.phone_model.value];

        return phoneModels.some((model) => this.isExactPhoneModelMatch(model, phoneModel));
      }

      return false;
    });

    // Return compatible variant, or first available variant, or first variant as fallback
    return compatibleVariant || product.variants?.find((v) => v.available) || product.variants?.[0];
  }

  formatMoneyUpsell(price: number | string | null): string {
    // Handle invalid prices first
    if (price === null || price === undefined) {
      return "$0.00";
    }

    const isStringPrice = typeof price === "string";
    let numPrice = isStringPrice ? parseFloat(price) : (price as number);

    // Handle invalid prices
    if (isNaN(numPrice) || numPrice < 0) {
      return "$0.00";
    }

    // Collections API returns string prices in dollars (e.g., "44.95")
    // Products API returns integer prices in cents (e.g., 4495)
    if (isStringPrice) {
      // Don't divide by 100 for string prices
    } else if (numPrice >= 100 && Number.isInteger(numPrice)) {
      // If it's a large integer, it's likely in cents
      numPrice = numPrice / 100;
    }

    const formatted = new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(numPrice);
    return formatted.replace("$", "").trim();
  }

  getImageUrl(imageUrl: string, width = 120): string {
    if (!imageUrl) return "";

    // Ensure we have a string URL
    if (typeof imageUrl !== "string") {
      return "";
    }

    // If it's already a full URL, use it as is
    if (imageUrl.startsWith("http")) {
      // Add Shopify image transformation if it's a Shopify CDN URL
      if (imageUrl.includes("cdn.shopify.com")) {
        // Remove any existing size parameters and add new width
        const baseUrl = imageUrl.split("?")[0];
        return `${baseUrl}?width=${width}`;
      }
      return imageUrl;
    }

    // If it's a relative path, make it absolute and add transformation
    const cleanUrl = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
    return `${cleanUrl}?width=${width}`;
  }

  // Cache management
  getFromCache(key: string): unknown {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > this.config.cacheTimeout) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  setCache(key: string, data: unknown, _timeout?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  showLoadingState(): void {
    if (this.upsellContainer) {
      // Liquid 'loading-spinner' snippet (pastille_loading.svg); no custom spinner fallback.
      const loadingSpinner = window.cartUpsellStrings?.loadingSpinner ?? "";

      this.upsellContainer.innerHTML = `
        <div class="cart-upsells-loading">
          <div class="cart-upsells-loading__content">
            ${loadingSpinner}
            <p class="cart-upsells-loading__text">${this.getLocalizedText("loading_rec")}</p>
          </div>
        </div>
      `;
    }
  }

  hideLoadingState(): void {
    const loading = document.querySelector(".cart-upsells-loading");
    if (loading) loading.remove();
  }

  isCartDrawerOpen(): boolean {
    const drawer = document.querySelector("cart-drawer");
    return drawer?.classList.contains("active") || drawer?.hasAttribute("open") || false;
  }

  getLocalizedText(key: string): string {
    const texts = {
      en: {
        pc_title: "Complete your kit",
        add_to_cart: "Add",
        ensemble_title: "Complete the set",
        rec_title: "You might also like",
        same_name_title: "Also available in this design",
        loading_rec: "Loading recommendations...",
        adding: "Adding",
        added: "Added!",
        error_retry: "Error - Try again",
        sold_out: "Sold out",
        no_recommendations: "No recommendations available",
      },
      fr: {
        pc_title: "Complétez votre kit",
        add_to_cart: "Ajouter",
        ensemble_title: "Complétez l'ensemble",
        rec_title: "Vous pourriez aussi aimer",
        same_name_title: "Aussi offert dans ce design",
        loading_rec: "Chargement des recommandations...",
        adding: "Ajout",
        added: "Ajouté !",
        error_retry: "Erreur - Réessayez",
        sold_out: "Épuisé",
        no_recommendations: "Aucune recommandation disponible",
      },
    };

    const locale = document.documentElement.lang || "en";
    const localeTexts = locale in texts ? texts[locale as "en" | "fr"] : texts.en;
    return (localeTexts as Record<string, string>)[key] || (texts.en as Record<string, string>)[key] || key;
  }

  formatMoney(cents: number): string {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  }

  async fetchCartDrawerSection(): Promise<string | undefined> {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("sections", "cart-drawer-optimized-v2");
      const response = await fetch(url.pathname + url.search);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const sections = await response.json();
      return sections?.["cart-drawer-optimized-v2"] || undefined;
    } catch (error) {
      console.warn("Failed to fetch cart drawer section:", error);
      return undefined;
    }
  }

  applyCartDrawerSection(sectionHtml: string): void {
    try {
      const html = new DOMParser().parseFromString(sectionHtml, "text/html");

      // Try to update each section, starting with the most specific
      let updated = false;

      // Update cart drawer sections including blocks
      const sectionsToUpdate = [
        "cart-drawer-items",
        ".drawer__footer", // Use the main footer container instead of cart-drawer__footer
      ];

      for (const selector of sectionsToUpdate) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
          updated = true;
        } else {
          // Only warn if it's a critical section
          if (selector === "cart-drawer-items") {
            console.warn(`❌ Failed to update ${selector}`);
          }
        }
      }

      // Also update the blocks section (between header and cart-content-container)
      const drawerInner = document.querySelector(".drawer__inner");
      const sourceDrawerInner = html.querySelector(".drawer__inner");
      if (drawerInner && sourceDrawerInner) {
        // Find the blocks section (everything between header and cart-content-container)
        const currentHeader = drawerInner.querySelector(".drawer__header");
        const currentCartContent = drawerInner.querySelector("cart-content-container");
        const sourceHeader = sourceDrawerInner.querySelector(".drawer__header");
        const sourceCartContent = sourceDrawerInner.querySelector("cart-content-container");

        if (currentHeader && currentCartContent && sourceHeader && sourceCartContent) {
          // Remove everything between header and cart-content-container
          let nextElement = currentHeader.nextElementSibling;
          while (nextElement && nextElement !== currentCartContent) {
            const elementToRemove = nextElement;
            nextElement = nextElement.nextElementSibling;
            elementToRemove.remove();
          }

          // Insert new blocks from source
          let sourceElement = sourceHeader.nextElementSibling;
          while (sourceElement && sourceElement !== sourceCartContent) {
            const elementToInsert = sourceElement.cloneNode(true);
            currentCartContent.parentNode!.insertBefore(elementToInsert, currentCartContent);
            sourceElement = sourceElement.nextElementSibling;
          }

          updated = true;
        }
      }

      // If specific updates didn't work, try updating the full cart drawer
      if (!updated) {
        const cartDrawerTarget = document.querySelector("#CartDrawer");
        const cartDrawerSource = html.querySelector("#CartDrawer");
        if (cartDrawerTarget && cartDrawerSource) {
          cartDrawerTarget.innerHTML = cartDrawerSource.innerHTML;
          updated = true;
        }
      }

      // Only called right after a successful add, so the cart cannot be empty.
      document.querySelector("cart-drawer")?.classList.remove("is-empty");

      // The caller (submitUpsellForm) dispatches cart:updated right after, which triggers the single refresh.
      if (!updated) console.warn("❌ Cart content update failed");
    } catch (error) {
      console.warn("Failed to update cart drawer content:", error);
    }
  }
}

window.handleUpsellProductChange = function (event: Event): void {
  const select = event.target as HTMLSelectElement;
  const variantId = select.value;
  const form = select.closest(".upsell-product")!.querySelector("form") as HTMLFormElement;
  const variantInput = form.querySelector(".product-variant-id") as HTMLInputElement | null;

  if (variantInput) {
    variantInput.value = variantId;
  }

  const selectedOption = select.options[select.selectedIndex];
  const isAvailable = selectedOption.getAttribute("data-available") === "true";
  const button = form.querySelector('button[type="submit"]') as HTMLButtonElement;
  const buttonText = button.querySelector("span") as HTMLSpanElement;

  if (isAvailable) {
    button.disabled = false;
    buttonText.textContent = window.cartUpsellStrings?.addToCart || "Add to cart";
  } else {
    button.disabled = true;
    buttonText.textContent = window.cartUpsellEngine?.getLocalizedText("sold_out") ?? "Sold out";
  }
};

// Explicit window assignment: without it, Rollup's tree-shaker drops this
// class entirely since nothing inside this module references it — it's only
// ever instantiated from the inline script in cart-drawer-optimized-v2.liquid.
window.CartUpsellEngine = CartUpsellEngine;
// Initialization is handled by the cart drawer section
